import { handleError } from '../context/errors/errorUtils';
import { fetchData } from '../api/api';

const createDialogFormData = (data) => {
  const formData = new FormData();
  const allowedFields = [
    'dialogId',
    'step',
    'userId',
    'correctAnswer',
    'matchedOptionIndex',
    'recognizedText',
    'attemptNumber',
  ];

  if (data.audioBuffer) {
    formData.append('audio', new Blob([data.audioBuffer], { type: 'audio/wav' }), 'audio.wav');
  }

  Object.entries(data).forEach(([key, value]) => {
    if (allowedFields.includes(key) && value !== undefined) {
      formData.append(key, value);
    }
  });

  return formData;
};

export const sendDialogData = async (data = {}) => {
  const { dialogId, step, userId } = data;
  const formData = createDialogFormData(data);

  try {
    return await fetchData('/api/dialogs', 'POST', formData);
  } catch (error) {
    handleError({
      code: 'SEND_DIALOG_DATA_ERROR',
      error,
      context: {
        module: 'sendDialogData',
        dialogId,
        userId,
        step,
      },
      showToUser: false,
    });
    throw error;
  }
};

export const fetchDialogs = async ({ dialogId, userId, dispatch }) => {
  try {
    const data = await sendDialogData({ dialogId, userId });

    if (data.error) {
      handleError({
        code: 'BACKEND_RESPONSE_ERROR',
        error: data.error,
        context: {
          module: 'fetchDialogs',
          dialogId,
          userId,
        },
        showToUser: true,
      });
      dispatch({ type: 'SET_ERROR', payload: data.error });
      return;
    }

    const currentStep = data.step ?? data.dialogStepId;
    const expectedStepId = 1;

    if (currentStep !== expectedStepId) {
      const message = `Invalid dialog step id: expected ${expectedStepId}, got ${currentStep}`;
      handleError({
        code: 'UNEXPECTED_STEP_ID',
        error: new Error(message),
        context: {
          module: 'fetchDialogs',
          expectedStepId,
          receivedStepId: currentStep,
          dialogId,
          userId,
        },
        showToUser: true,
      });
      dispatch({ type: 'SET_ERROR', payload: message });
      return;
    }

    const introData = currentStep === 1
      ? {
          introText: data.introText || '',
          introTranslate: data.introTranslate || '',
          introAudio: data.introAudio || '',
          introImage: data.introImage || '',
        }
      : null;

    const dialogStep = {
      id: currentStep,
      dialogId,
      assistantText: data.assistantText || '',
      assistantTextTranslate: data.assistantTextTranslate || '',
      assistantAudioUrl: data.assistantAudioUrl || '',
      options: Array.isArray(data.options)
        ? data.options.map((option, index) => ({ ...option, index }))
        : [],
      language: data.language || 'en',
      totalSteps: data.totalSteps || 6,
      topic: data.learnDialogTitle || '',
    };

    dispatch({
      type: 'SET_INITIAL_DATA',
      payload: {
        intro: introData,
        steps: [dialogStep],
        totalSteps: data.totalSteps || 0,
        language: data.language || 'en',
        currentStep,
      },
    });
  } catch (error) {
    handleError({
      code: 'DIALOG_FETCH_ERROR',
      error,
      context: {
        module: 'fetchDialogs',
        dialogId,
        userId,
      },
      showToUser: true,
    });
    dispatch({ type: 'SET_ERROR', payload: 'Failed to load dialog. Please try again.' });
  }
};
